import { useState } from "react";
import { Menu, X, ChevronRight, Phone, Mail, Clock, MapPin, User, MessageSquare, Send, AlertCircle } from "lucide-react";

export default function Contact() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error when field is being edited
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ""
      });
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }
    
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }
    
    if (!formData.message.trim()) {
      errors.message = "Message is required";
    }
    
    return errors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const errors = validateForm();
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: ""
      });
      
      // Reset submission status after 5 seconds
      setTimeout(() => {
        setSubmitted(false);
      }, 5000);
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        {/* Announcement bar */}
        <div className="bg-gradient-to-r from-[#002D62] to-[#0077B6] text-white text-center py-2 text-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="font-semibold mr-2">Limited Time:</span> Get up to 30% off your first year of coverage. 
            <a href="/promotion" className="font-bold underline ml-1 hover:text-blue-100 transition-colors">Learn more</a>
          </div>
        </div>
        
        <div className="max-w-9xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20">
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center">
                <div className="mr-2 text-[#0077B6]">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-8 h-8">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <span className="text-2xl font-extrabold bg-gradient-to-r from-[#002D62] to-[#0077B6] bg-clip-text text-transparent">Optimal Actuarion</span>
              </div>
              <nav className="hidden md:ml-10 md:flex md:space-x-8">
              <a href="/" className="text-gray-700 hover:text-[#002D62] font-medium px-1 pt-2 pb-1 border-b-2 border-transparent hover:border-[#0077B6] transition-colors">Home</a>
                <a href="/#/about" className="text-gray-700 hover:text-[#002D62] font-medium px-1 pt-2 pb-1 border-b-2 border-transparent hover:border-[#0077B6] transition-colors">About Us</a>
                <a href="/#/services" className="text-gray-700 hover:text-[#002D62] font-medium px-1 pt-2 pb-1 border-b-2 border-transparent hover:border-[#0077B6] transition-colors">Services</a>
                <a href="/#/quote" className="text-gray-700 hover:text-[#002D62] font-medium px-1 pt-2 pb-1 border-b-2 border-transparent hover:border-[#0077B6] transition-colors">Get a Quote</a>
                <a href="/#/faqs" className="text-gray-700 hover:text-[#002D62] font-medium px-1 pt-2 pb-1 border-b-2 border-transparent hover:border-[#0077B6] transition-colors">FAQs</a>
                <a href="/#/testimonials" className="text-gray-700 hover:text-[#002D62] font-medium px-1 pt-2 pb-1 border-b-2 border-transparent hover:border-[#0077B6] transition-colors">Testimonials</a>
                <a href="/#/contact" className="text-[#002D62] font-bold border-b-2 border-[#0077B6] px-1 pt-2 pb-1 transition-colors">Contact</a>
              </nav>
            </div>
            <div className="flex items-center">
              <div className="hidden md:flex md:items-center md:space-x-4">
                <a href="/login" className="text-[#002D62] font-medium hover:text-[#0077B6] mr-4 transition-colors">Login</a>
                <a href="/quote" className="bg-gradient-to-r from-[#0077B6] to-[#00A8E8] text-white px-6 py-3 rounded-md font-bold hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200">
                  Get a Free Quote
                </a>
              </div>
              <div className="flex md:hidden">
                <button
                  type="button"
                  className="text-gray-700 hover:text-[#002D62] transition-colors"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-expanded={mobileMenuOpen}
                >
                  {mobileMenuOpen ? (
                    <X className="h-6 w-6" />
                  ) : (
                    <Menu className="h-6 w-6" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 shadow-lg animate-fadeIn">
            <div className="pt-2 pb-4 space-y-1 px-4">
            <a href="/" className="block py-2 text-gray-700 font-medium border-l-4 border-transparent hover:border-[#0077B6] hover:bg-gray-50 hover:text-[#002D62] pl-3 transition-colors">Home</a>
              <a href="/#/about" className="block py-2 text-gray-700 font-medium border-l-4 border-transparent hover:border-[#0077B6] hover:bg-gray-50 hover:text-[#002D62] pl-3 transition-colors">About Us</a>
              <a href="/#/services" className="block py-2 text-gray-700 font-medium border-l-4 border-transparent hover:border-[#0077B6] hover:bg-gray-50 hover:text-[#002D62] pl-3 transition-colors">Services</a>
              <a href="/#/quote" className="block py-2 text-gray-700 font-medium border-l-4 border-transparent hover:border-[#0077B6] hover:bg-gray-50 hover:text-[#002D62] pl-3 transition-colors">Get a Quote</a>
              <a href="/#/faqs" className="block py-2 text-gray-700 font-medium border-l-4 border-transparent hover:border-[#0077B6] hover:bg-gray-50 hover:text-[#002D62] pl-3 transition-colors">FAQs</a>
              <a href="/#/testimonials" className="block py-2 text-gray-700 font-medium border-l-4 border-transparent hover:border-[#0077B6] hover:bg-gray-50 hover:text-[#002D62] pl-3 transition-colors">Testimonials</a>
              <a href="/#/contact" className="block py-2 text-[#002D62] font-bold border-l-4 border-[#0077B6] pl-3">Contact</a>
              <div className="pt-4 pb-2 space-y-2">
                <a href="/login" className="block text-center text-[#002D62] font-medium hover:text-[#0077B6] py-2 transition-colors">
                  Login
                </a>
                <a href="/quote" className="w-full flex items-center justify-center bg-gradient-to-r from-[#0077B6] to-[#00A8E8] text-white px-4 py-3 rounded-md font-bold shadow-md transition-all hover:shadow-lg">
                  Get a Free Quote
                </a>
              </div>
            </div>
          </div>
        )}
      </header>

      <main>
        {/* Page header */}
        <div className="bg-gradient-to-r from-[#002D62] to-[#0077B6] text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold">Contact Us</h1>
              <p className="mt-4 text-lg md:text-xl max-w-2xl mx-auto">
                Have questions about our insurance services? We're here to help. Reach out to our friendly team today.
              </p>
            </div>
          </div>
        </div>
        
        {/* Contact information and form */}
        <div className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact information */}
              <div>
                <h2 className="text-2xl font-bold text-[#002D62] mb-6">Get In Touch</h2>
                
                <div className="bg-[#E8F1F9] rounded-lg p-6 mb-8">
                  <div className="flex flex-col space-y-6">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="p-3 bg-[#0077B6] rounded-full text-white">
                          <Phone className="h-6 w-6" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-[#002D62]">Phone</h3>
                        <p className="mt-1 text-gray-700">972-891-8635</p>
                        <p className="text-sm text-gray-500 mt-1">Monday-Friday: 9:00 AM - 5:00 PM</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="p-3 bg-[#0077B6] rounded-full text-white">
                          <Mail className="h-6 w-6" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-[#002D62]">Email</h3>
                        <p className="mt-1 text-gray-700">OptimalActuarion@gmail.com</p>
                        <p className="text-sm text-gray-500 mt-1">We'll respond as soon as possible</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="p-3 bg-[#0077B6] rounded-full text-white">
                          <MapPin className="h-6 w-6" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-[#002D62]">Office Location</h3>
                        <p className="mt-1 text-gray-700">
                          3221 Finley Road<br />
                          Irving, TX 75062
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="p-3 bg-[#0077B6] rounded-full text-white">
                          <Mail className="h-6 w-6" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-[#002D62]">Business Address</h3>
                        <p className="mt-1 text-gray-700">
                          4020 N MacArthur Blvd<br />
                          Ste 122 #7156<br />
                          Irving, TX 75038
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <div className="p-3 bg-[#0077B6] rounded-full text-white">
                          <Clock className="h-6 w-6" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg font-medium text-[#002D62]">Business Hours</h3>
                        <p className="mt-1 text-gray-700">Monday-Friday: 9:00 AM - 5:00 PM</p>
                        <p className="text-gray-700">Saturday-Sunday: Closed</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Map placeholder */}
                <div className="rounded-lg overflow-hidden shadow-md h-64 bg-gray-200">
                  <img src="/api/placeholder/800/300" alt="Location Map" className="w-full h-full object-cover" />
                </div>
              </div>
              
              {/* Contact form */}
              <div>
                <h2 className="text-2xl font-bold text-[#002D62] mb-6">Send Us a Message</h2>
                
                {submitted ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                      <svg className="h-6 w-6 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h3 className="mt-3 text-lg font-medium text-green-800">Thank you for your message!</h3>
                    <p className="mt-2 text-sm text-green-700">
                      We've received your inquiry and will get back to you as soon as possible.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="bg-[#F4F4F4] rounded-lg p-6">
                    <div className="grid grid-cols-1 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-1 relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <User className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            type="text"
                            name="name"
                            id="name"
                            value={formData.name}
                            onChange={handleChange}
                            className={`pl-10 block w-full shadow-sm rounded-md py-3 px-4 border ${
                              formErrors.name ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-[#0077B6] focus:border-[#0077B6]'
                            }`}
                            placeholder="John Doe"
                          />
                        </div>
                        {formErrors.name && (
                          <p className="mt-1 text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {formErrors.name}
                          </p>
                        )}
                      </div>
                      
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-1 relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Mail className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            type="email"
                            name="email"
                            id="email"
                            value={formData.email}
                            onChange={handleChange}
                            className={`pl-10 block w-full shadow-sm rounded-md py-3 px-4 border ${
                              formErrors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-[#0077B6] focus:border-[#0077B6]'
                            }`}
                            placeholder="your.email@example.com"
                          />
                        </div>
                        {formErrors.email && (
                          <p className="mt-1 text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {formErrors.email}
                          </p>
                        )}
                      </div>
                      
                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                          Phone Number
                        </label>
                        <div className="mt-1 relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Phone className="h-5 w-5 text-gray-400" />
                          </div>
                          <input
                            type="tel"
                            name="phone"
                            id="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            className="pl-10 block w-full shadow-sm rounded-md py-3 px-4 border border-gray-300 focus:ring-[#0077B6] focus:border-[#0077B6]"
                            placeholder="(123) 456-7890"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                          Subject
                        </label>
                        <div className="mt-1">
                          <select
                            id="subject"
                            name="subject"
                            value={formData.subject}
                            onChange={handleChange}
                            className="block w-full shadow-sm rounded-md py-3 px-4 border border-gray-300 focus:ring-[#0077B6] focus:border-[#0077B6]"
                          >
                            <option value="">Select a topic</option>
                            <option value="Health Insurance">Health Insurance</option>
                            <option value="Life Insurance">Life Insurance</option>
                            <option value="Senior Citizen Plans">Senior Citizen Plans</option>
                            <option value="Quote Request">Quote Request</option>
                            <option value="Customer Support">Customer Support</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                          Message <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-1 relative">
                          <div className="absolute inset-y-0 left-0 pl-3 pt-3 flex items-start pointer-events-none">
                            <MessageSquare className="h-5 w-5 text-gray-400" />
                          </div>
                          <textarea
                            id="message"
                            name="message"
                            rows="4"
                            value={formData.message}
                            onChange={handleChange}
                            className={`pl-10 block w-full shadow-sm rounded-md py-3 px-4 border ${
                              formErrors.message ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300 focus:ring-[#0077B6] focus:border-[#0077B6]'
                            }`}
                            placeholder="How can we help you?"
                          ></textarea>
                        </div>
                        {formErrors.message && (
                          <p className="mt-1 text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {formErrors.message}
                          </p>
                        )}
                      </div>
                      
                      <div>
                        <button
                          type="submit"
                          disabled={submitting}
                          className={`w-full inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white bg-gradient-to-r from-[#0077B6] to-[#00A8E8] hover:shadow-lg transition-all ${
                            submitting ? 'opacity-70 cursor-not-allowed' : 'transform hover:-translate-y-0.5'
                          }`}
                        >
                          {submitting ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Processing...
                            </>
                          ) : (
                            <>
                              Send Message
                              <Send className="ml-2 h-5 w-5" />
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                )}
                
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-blue-700">
                        Need immediate assistance? Call us directly at <span className="font-medium">972-891-8635</span> during business hours.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* FAQ section */}
        <div className="bg-[#E8F1F9] py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-[#002D62]">Frequently Asked Contact Questions</h2>
              <p className="mt-4 text-lg text-gray-700 max-w-2xl mx-auto">
                Find quick answers to common questions about contacting our team and receiving support.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-medium text-[#002D62]">How quickly will I receive a response?</h3>
                <p className="mt-2 text-gray-600">
                  We aim to respond to all inquiries within 24-48 business hours. For urgent matters, we recommend calling our office directly at 972-891-8635.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-medium text-[#002D62]">Can I schedule a video consultation?</h3>
                <p className="mt-2 text-gray-600">
                  Yes! Please mention in your message that you'd prefer a video consultation, and our team will coordinate a convenient time for a virtual meeting.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-medium text-[#002D62]">Do you offer in-person appointments?</h3>
                <p className="mt-2 text-gray-600">
                  Yes, we welcome in-person meetings at our Irving office. Please contact us to schedule an appointment during our business hours.
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-medium text-[#002D62]">How can I get an insurance quote?</h3>
                <p className="mt-2 text-gray-600">
                  You can request a quote through our <a href="/quote" className="text-[#0077B6] hover:underline">quote form</a>, by phone, or by email. We'll need some basic information to provide you with an accurate estimate.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#002D62] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Optimal Actuarion LLC</h3>
              <p className="mb-4">Insurance solutions for everyone, with a special focus on senior citizens and underserved communities.</p>
              <div className="flex flex-col space-y-2">
                <a href="/about" className="hover:text-gray-300">About Us</a>
                <a href="/services" className="hover:text-gray-300">Services</a>
                <a href="/testimonials" className="hover:text-gray-300">Testimonials</a>
                <a href="/faqs" className="hover:text-gray-300">FAQs</a>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">Contact Information</h3>
              <div className="flex flex-col space-y-2">
                <div className="flex items-center">
                  <Phone className="h-4 w-4 mr-2" />
                  <span>972-891-8635</span>
                </div>
                <div className="flex items-center">
                  <Mail className="h-4 w-4 mr-2" />
                  <span>OptimalActuarion@gmail.com</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-2" />
                  <span>4020 N MacArthur Blvd, Ste 122 #7156<br />Irving, TX 75038</span>
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-2" />
                  <span>Mon-Fri: 9:00 AM - 5:00 PM</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-xl font-bold mb-4">Quick Links</h3>
              <div className="grid grid-cols-2 gap-2">
                <a href="/health-insurance" className="hover:text-gray-300 flex items-center">
                  <ChevronRight className="h-4 w-4 mr-1" />
                  Health Insurance
                </a>
                <a href="/life-insurance" className="hover:text-gray-300 flex items-center">
                  <ChevronRight className="h-4 w-4 mr-1" />
                  Life Insurance
                </a>
                <a href="/senior-plans" className="hover:text-gray-300 flex items-center">
                  <ChevronRight className="h-4 w-4 mr-1" />
                  Senior Plans
                </a>
                <a href="/disability" className="hover:text-gray-300 flex items-center">
                  <ChevronRight className="h-4 w-4 mr-1" />
                  Disability
                </a>
                <a href="/quote" className="hover:text-gray-300 flex items-center">
                  <ChevronRight className="h-4 w-4 mr-1" />
                  Get a Quote
                </a>
                <a href="/contact" className="hover:text-gray-300 flex items-center">
                  <ChevronRight className="h-4 w-4 mr-1" />
                  Contact Us
                </a>
              </div>
            </div>
          </div>
          
          <div className="mt-8 pt-8 border-t border-blue-700">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="mb-4 md:mb-0">
                <p>&copy; {new Date().getFullYear()} Optimal Actuarion LLC. All rights reserved.</p>
              </div>
              
              <div className="flex space-x-6">
                <a href="/privacy" className="hover:text-gray-300">Privacy Policy</a>
                <a href="/terms" className="hover:text-gray-300">Terms of Service</a>
                <a href="/accessibility" className="hover:text-gray-300">Accessibility</a>
              </div>
            </div>
            
            <div className="mt-4 text-sm text-gray-300 text-center">
              <p>Optimal Actuarion LLC is licensed in Texas (TX), Florida (FL), and California (CA).</p>
              <p className="mt-2">Insurance products and services offered through Optimal Actuarion LLC are not insured by any federal government agency.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}